import { addBusinessDays } from 'date-fns'

import {
  ObservationEvent,
  ObservationFrequency,
} from '@/pages/instruments/new-instrument/schedule-builders/types'

/**
 * Build a schedule of calendar-based observation dates
 * at a fixed frequency, stopping at maturity.
 *
 * Might include a stub period at the end dependent on maturity date.
 *
 * Last observation date might not be exactly the same period as the others.
 */
const getScheduledDates = ({
  initial_observation_date,
  maturity_date,
  yearly_observations,
}: {
  initial_observation_date: Date
  maturity_date: Date
  yearly_observations: ObservationFrequency
}): Date[] => {
  const _dates: Date[] = []
  const monthlyStep = 12 / yearly_observations

  let i = 1

  while (true) {
    const currentDate = new Date(initial_observation_date)
    currentDate.setMonth(currentDate.getMonth() + monthlyStep * i)

    if (currentDate > maturity_date) {
      break
    }

    const day = currentDate.getDay()
    if (day === 6) currentDate.setDate(currentDate.getDate() + 2)
    if (day === 0) currentDate.setDate(currentDate.getDate() + 1)

    _dates.push(currentDate)
    i++
  }

  return _dates
}

// /**
//  * Get the observation dates for the schedule, and their pay dates.
//  */
// export const getObservationEvents = ({
//   initial_observation_date,
//   maturity_date,
//   settlement_cycle,
//   yearly_observations,
// }: {
//   initial_observation_date: Date
//   maturity_date: Date
//   settlement_cycle: number
//   yearly_observations: ObservationFrequency
// }): ObservationEvent[] => {
//   const observationDates = getScheduledDates({
//     initial_observation_date,
//     maturity_date,
//     yearly_observations,
//   })

//   return observationDates.map((date) => ({
//     date,
//     payDate: addBusinessDays(date, settlement_cycle),
//   }))
// }

/**
 * Get the observation dates for the schedule, and their pay dates.
 */
export const _getObservationEvents = ({
  initial_observation_date,
  maturity_date,
  settlement_cycle,
  autocall_yearly_observations,
  coupon_yearly_observations,
}: {
  initial_observation_date: Date
  maturity_date: Date
  settlement_cycle: number
  autocall_yearly_observations: ObservationFrequency
  coupon_yearly_observations: ObservationFrequency
}): ObservationEvent[] => {
  const observationDates = getScheduledDates({
    initial_observation_date,
    maturity_date,
    yearly_observations: autocall_yearly_observations,
  })

  const couponDates = getScheduledDates({
    initial_observation_date,
    maturity_date,
    yearly_observations: coupon_yearly_observations,
  })

  const allDates = Array.from(
    new Set([...observationDates, ...couponDates].map((d) => d.getTime())),
  )
    .sort((a, b) => a - b)
    .map((t) => new Date(t))

  return allDates.map((date) => ({
    date,
    payDate: addBusinessDays(date, settlement_cycle),
    isCoupon: couponDates.map((date) => date.getTime()).includes(date.getTime()),
    isAutocall: observationDates.map((date) => date.getTime()).includes(date.getTime()),
    couponRate: null,
  }))
}

export const _applyCouponSchedule = ({
  events,
  coupon_rate_annual,
  coupon_yearly_observations,
  accrue_coupon = false,
}: {
  events: ObservationEvent[]
  coupon_rate_annual: number
  coupon_yearly_observations: ObservationFrequency
  accrue_coupon?: boolean
}) => {
  const coupon_increment = coupon_rate_annual / coupon_yearly_observations
  let accrued = 0

  return events.map((event) => {
    if (event.isCoupon) {
      accrued += coupon_increment
    }

    return {
      ...event,
      couponRate: event.isCoupon ? (accrue_coupon ? accrued : coupon_rate_annual) : null,
    }
  })
}

// /**
//  * Applies coupon information to the schedule, given existing observation dates.
//  */
// export const applyCouponSchedule = ({
//   events,
//   initial_observation_date,
//   maturity_date,
//   coupon_rate_annual,
//   coupon_yearly_observations,
//   accrue_coupon = false,
// }: {
//   events: ObservationEvent[]
//   initial_observation_date: Date
//   maturity_date: Date
//   coupon_rate_annual: number
//   coupon_yearly_observations: ObservationFrequency
//   accrue_coupon?: boolean
// }): ObservationEventWithCoupon[] => {
//   // How much the coupon increases each time
//   const increment = coupon_rate_annual / coupon_yearly_observations

//   const couponDates = getScheduledDates({
//     initial_observation_date,
//     maturity_date,
//     yearly_observations: coupon_yearly_observations,
//   })

//   let accrued = 0

//   return events.map((event) => {
//     const isCoupon = couponDates.some((d) => d.getTime() === event.date.getTime())

//     if (isCoupon) {
//       accrued += increment
//     }

//     return {
//       ...event,
//       isCoupon,
//       couponRate: isCoupon ? (accrue_coupon ? accrued : coupon_rate_annual) : null,
//     }
//   })
// }
